/**
 * SW2-2459 (PR 2) — signing a downloaded project that still has offline work.
 *
 * A signature makes the server build and email the Inspection Complete report from what it holds
 * at that moment. On a downloaded project with work still on the device (or a sync running), a
 * signature sent straight away emails a report without that work. Such a signature is queued
 * instead, like an offline one, and the sync sends it after the inspection's other work
 * (offlineProjectSync `syncInspectionWork`). Online or offline, the rule is the same.
 */
import db, { countUnsyncedWork } from '../db/db';

/**
 * Lazy: the coordinator imports the sync, which imports the certification API, which imports this
 * file — a static import here would close that loop.
 */
const syncing = async (projectId) => (await import('./offlineSyncCoordinator')).isSyncing(projectId);

/**
 * The downloaded project a signature on this inspection must wait for, or null when it can be
 * sent now: the project is not on this device, or nothing of it is waiting and no sync is running.
 *
 * Throws when the inspection is on the device but its offline work can't be checked: signing
 * then could email a report without that work (PR #156 review). Only when the device can't even
 * be asked whether it holds the inspection does it sign directly, as before this change — failing
 * there would block signing for everyone whose browser storage is broken, downloads or not.
 */
export async function projectToWaitFor(inspectionId) {
  let inspection;

  try {
    inspection = await db.inspections.get(Number(inspectionId));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Could not read inspection ${inspectionId} on this device; signing directly`, err);

    return null;
  }

  const projectId = inspection?.project_id;

  if (!projectId) return null;

  try {
    const project = await db.projects.get(projectId);

    if (!project) return null;

    // Only the user who downloaded the project queues on it: the sync uploads a download only for
    // its owner (PR 1), so a signature queued by anyone else would wait on their session — or be
    // sent under it. A download from before `downloadedBy` is claimed by the user signing on it.
    const userId = (await db.user.get(1))?.user_id;

    // Who is signing can't be told, so neither can whether to wait (PR #156 review).
    if (userId == null) throw new Error('No user is stored on this device');

    if (project.downloadedBy == null) {
      await db.projects.update(projectId, { downloadedBy: userId });
    } else if (project.downloadedBy !== userId) {
      return null;
    }

    if ((await syncing(projectId)) || (await countUnsyncedWork(projectId)) > 0) return projectId;

    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Could not check offline work for inspection ${inspectionId}`, err);

    // The caller adds "Please try again."
    throw new Error("This inspection's offline work could not be checked on this device, so it was not signed");
  }
}

/**
 * Throws if this inspection's download belongs to another user. Saved offline there, a signature
 * would never be sent — the sync uploads a download only for its owner — or, if they synced it,
 * would go up under their name (PR #156 review). Online it is simply sent, so only the offline save
 * calls this. Anything it can't read is left to the save, which fails loudly itself.
 */
export async function ensureOwnDownload(inspectionId) {
  let owner;

  let userId;

  try {
    const inspection = await db.inspections.get(Number(inspectionId));
    const project = inspection?.project_id && (await db.projects.get(inspection.project_id));

    owner = project?.downloadedBy;
    userId = (await db.user.get(1))?.user_id;
  } catch (err) {
    return;
  }

  if (owner != null && userId != null && owner !== userId) {
    throw new Error(
      'This project was downloaded on this device by another user, so a signature can\'t be saved here offline. Sign it once you\'re back online',
    );
  }
}

/**
 * Signatures queued on the device, shown on top of the server's answer: until the sync sends
 * them the server doesn't have them, and an unsigned line would invite signing it twice. Each one
 * is marked `queued` so the line can say it hasn't been sent yet.
 */
export function withQueuedSignatures(serverState, localRow) {
  const queue = localRow?.sync?.synced === false ? localRow.sync.payload || [] : [];

  if (!serverState || !queue.length) return serverState;

  const userSignatures = { ...(serverState.userSignatures || {}) };

  for (const entry of queue) {
    const local = (localRow.userSignatures?.[entry.group] || []).find((s) => s.id === entry.id);
    const signed = userSignatures[entry.group] || [];

    if (local && !signed.some((s) => s.lineType === local.lineType)) {
      userSignatures[entry.group] = [...signed, { ...local, queued: true }];
    }
  }

  return { ...serverState, userSignatures };
}

/**
 * The device's own row, as shown offline: its queued signatures are already in it, but must be
 * marked `queued` too, or they look sent (PR #156 review).
 */
export function markQueuedSignatures(localRow) {
  const queue = localRow?.sync?.synced === false ? localRow.sync.payload || [] : [];

  if (!queue.length) return localRow;

  const userSignatures = { ...(localRow.userSignatures || {}) };

  for (const entry of queue) {
    const signed = userSignatures[entry.group];

    if (signed) {
      userSignatures[entry.group] = signed.map((s) => (s.id === entry.id ? { ...s, queued: true } : s));
    }
  }

  return { ...localRow, userSignatures };
}

/**
 * Why a bulk sign must wait, or null. The bulk endpoints sign many inspections in one request and
 * have no queue, so an inspection of a downloaded project with offline work can't be signed there.
 * As for one signature: a project on the device whose work can't be checked blocks the sign too
 * (PR #156 review); only an unreadable inspection lookup lets it through.
 */
export async function offlineSignBlock(inspectionIds) {
  let projectIds;

  try {
    const inspections = (await db.inspections.bulkGet(inspectionIds.map(Number))).filter(Boolean);

    projectIds = [...new Set(inspections.map((i) => i.project_id).filter(Boolean))];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Could not read the selected inspections on this device; not blocking the bulk sign', err);

    return null;
  }

  for (const projectId of projectIds) {
    let project;

    try {
      project = await db.projects.get(projectId);

      if (!project) continue;

      // As for one signature: another user's download is signed directly — it is theirs to sync,
      // so blocking on it could never be lifted from this session (PR #156 review). A download from
      // before `downloadedBy` counts as this user's, as signing on it claims it.
      const userId = (await db.user.get(1))?.user_id;

      if (userId == null) throw new Error('No user is stored on this device');
      if (project.downloadedBy != null && project.downloadedBy !== userId) continue;

      if ((await syncing(projectId)) || (await countUnsyncedWork(projectId)) > 0) {
        return `${project.name || 'A downloaded project'} has offline work waiting to upload. Sync it first, then sign.`;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Could not check offline work for project ${projectId}`, err);

      return `${project?.name || 'A downloaded project'}'s offline work could not be checked on this device. Try again.`;
    }
  }

  return null;
}

/**
 * SW2-2459 × SW2-2248 — whether this inspection's Inspection Complete report waits for a review.
 * `review` is the cert state's block (`CertStateReview`): null when the form doesn't require review
 * (or the tenant feature is off), so the report goes out as soon as the signature does. Anything but
 * `approved` means signing raises (or rejoins) a review and the report waits for it: `not_raised`
 * before the first signature, `pending` while it waits, and `rejected` / `cancelled` because signing
 * again raises a fresh one.
 *
 * Only for what the signer is told before the server has answered — a queued signature has no answer
 * yet. Once it has, its `reviewRaised` is the authority.
 */
export const reviewHoldsReport = (review) => review != null && review.status !== 'approved';

/** The toast for a signature queued behind the project's offline work. */
export const queuedSignatureMessage = (review) =>
  reviewHoldsReport(review)
    ? `Signed. It will be sent once this project's offline work has uploaded, then go ${
        review.reviewerName ? `to ${review.reviewerName} ` : ''
      }for review — the inspection report is emailed once it's approved.`
    : "Signed. It will be sent once this project's offline work has uploaded — the inspection report goes out then.";

/** The note under a line whose signature is queued on this device. */
export const queuedSignatureHint = (review, raisesReview = false) => {
  // SW2-2248 decision 11's "after signing, unsynced" copy: this line's upload is what raises the
  // review. Only true of the first inspection line; every other line keeps the general wording.
  if (review?.status === 'not_raised' && raisesReview) {
    return `Signed on this device. Waiting to sync before review can start. ${
      review.reviewerName || 'The reviewer'
    } will be notified once this inspection uploads.`;
  }

  return reviewHoldsReport(review)
    ? 'Signed on this device — waiting to upload. It then goes for review; the inspection report goes out once it is approved.'
    : 'Signed on this device — waiting to upload. The inspection report goes out once it has.';
};
